import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Lock, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useFinanceAuth } from "@/context/FinanceAuthContext";

/**
 * Wrapper che protegge un'intera sezione dietro alla password Contabilità
 * (unica per Costi Fissi, Costi Variabili, Cash Flow, BEP, Situazione Economica).
 * Una volta sbloccata, resta valida per 60 minuti nel FinanceAuthContext
 * — poi si richiude automaticamente.
 */
export default function FinanceGate({ children }) {
  const { unlocked, verifyAndSet, clear } = useFinanceAuth();
  const [input, setInput] = useState("");
  const [showPw, setShowPw] = useState(false);

  const verify = useMutation({
    mutationFn: (password) => verifyAndSet(password),
    onSuccess: () => {
      setInput("");
      toast.success("Sezione Contabilità sbloccata");
    },
    onError: () => toast.error("Password errata"),
  });

  if (unlocked) {
    return (
      <>
        <div className="flex justify-end -mt-2 mb-2">
          <button
            onClick={clear}
            data-testid="finance-lock-btn"
            title="Blocca la sezione Contabilità"
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold border border-slate-200 hover:bg-slate-100 rounded-sm text-slate-600"
          >
            <ShieldCheck className="w-3 h-3 text-emerald-600" /> Contabilità sbloccata · Blocca
          </button>
        </div>
        {children}
      </>
    );
  }

  return (
    <div className="max-w-md mx-auto mt-8" data-testid="finance-gate">
      <div className="bg-white border border-slate-200 p-8 shadow-brutal rounded-md">
        <div className="w-14 h-14 mx-auto bg-slate-900 flex items-center justify-center mb-4 rounded-sm">
          <Lock className="w-7 h-7 text-orange-500" />
        </div>
        <div className="overline text-center">Area riservata</div>
        <h2 className="font-display text-2xl font-black text-center mb-2">Contabilità</h2>
        <p className="text-slate-600 text-sm text-center mb-6">
          Inserisci la password una sola volta per accedere a
          <b> Costi di gestione, Costi variabili, Cash Flow, BEP </b>
          e <b>Situazione economica</b>.
          La sessione resta valida <b>60 minuti</b> o fino a "Blocca".
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!input.trim()) return;
            verify.mutate(input);
          }}
          className="space-y-3"
        >
          <div className="relative">
            <input
              type={showPw ? "text" : "password"} data-no-uppercase
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Password Contabilità"
              data-testid="finance-gate-password"
              autoFocus
              className="w-full pr-10 pl-3 py-2.5 border border-slate-300 rounded-sm text-sm outline-none focus:border-orange-500"
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"
              tabIndex={-1}
              title={showPw ? "Nascondi" : "Mostra"}
            >
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <button
            type="submit"
            disabled={verify.isPending || !input.trim()}
            data-testid="finance-gate-submit"
            className="w-full py-2.5 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white font-semibold rounded-sm inline-flex items-center justify-center gap-2"
          >
            <ShieldCheck className="w-4 h-4" />
            {verify.isPending ? "Verifica…" : "Sblocca sezione"}
          </button>
        </form>
      </div>
    </div>
  );
}
